import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { LoadingContext } from '../LoadingContext';
import Loading from './Loading';
import Swal from 'sweetalert2';
import './WriteReview.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const WriteReview = () => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    rating: 0
  });
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [reviewStatus, setReviewStatus] = useState(null);

  const { isLoggedIn, authFetch } = useContext(AuthContext);
  const { setIsLoading } = useContext(LoadingContext);
  const navigate = useNavigate();

  const TITLE_MAX = 100;
  const CONTENT_MAX = 1800;
  const MAX_IMAGES = 3;

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    fetchExistingReview();
  }, [isLoggedIn]);

  const fetchExistingReview = async () => {
    setIsLoading(true);
    setLoading(true);

    try {
      const response = await authFetch(`${API_BASE_URL}/Reviews/my`);

      if (response.ok) {
        const reviewData = await response.json();
        setReviewStatus(reviewData.status);
        setReviewId(reviewData.reviewID);

        // One review per user. A rejected review is reported by the API as Pending,
        // so there is no re-submission path here - any existing review is read-only.
        setIsEditMode(true);
        setFormData({
          title: reviewData.title,
          content: reviewData.content,
          rating: reviewData.rating
        });
        setExistingImages(reviewData.images || []);
      } else if (response.status === 404) {
        // No existing review - create mode
        setIsEditMode(false);
        setReviewStatus(null);
      } else {
        throw new Error('Failed to fetch existing review');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Error',
        text: err.message,
        icon: 'error',
        confirmButtonColor: '#0288d1'
      });
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'title' && value.length > TITLE_MAX) return;
    if (name === 'content' && value.length > CONTENT_MAX) return;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStarClick = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + images.length + files.length - imagesToRemove.length;

    if (totalImages > MAX_IMAGES) {
      await Swal.fire({
        title: 'Too Many Images',
        text: `Maximum ${MAX_IMAGES} images allowed. You currently have ${existingImages.length + images.length - imagesToRemove.length} image(s).`,
        icon: 'warning',
        confirmButtonColor: '#0288d1'
      });
      return;
    }

    const validFiles = [];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const maxSize = 5 * 1024 * 1024;
    let hasError = false;

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        await Swal.fire({
          title: 'Invalid File Type',
          text: `Invalid file type: ${file.name}. Only JPG, JPEG, and PNG files are allowed.`,
          icon: 'error',
          confirmButtonColor: '#0288d1'
        });
        hasError = true;
        break;
      }

      if (file.size > maxSize) {
        await Swal.fire({
          title: 'File Too Large',
          text: `File too large: ${file.name}. Maximum size is 5MB.`,
          icon: 'error',
          confirmButtonColor: '#0288d1'
        });
        hasError = true;
        break;
      }

      validFiles.push(file);
    }

    if (!hasError && validFiles.length > 0) {
      setImages(prev => [...prev, ...validFiles]);

      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewImages(prev => [...prev, {
            file,
            url: e.target.result,
            isNew: true
          }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeNewImage = (index) => {
    const imageToRemove = images[index];
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviewImages(prev => prev.filter(preview => preview.file !== imageToRemove));
  };

  const removeExistingImage = (imageId) => {
    setImagesToRemove(prev => [...prev, imageId]);
  };

  const restoreExistingImage = (imageId) => {
    setImagesToRemove(prev => prev.filter(id => id !== imageId));
  };

  const validateForm = async () => {
    if (!formData.title.trim()) {
      await Swal.fire({
        title: 'Missing Title',
        text: 'Review title is required.',
        icon: 'warning',
        confirmButtonColor: '#0288d1'
      });
      return false;
    }

    if (!formData.content.trim()) {
      await Swal.fire({
        title: 'Missing Content',
        text: 'Review content is required.',
        icon: 'warning',
        confirmButtonColor: '#0288d1'
      });
      return false;
    }

    if (formData.rating === 0) {
      await Swal.fire({
        title: 'Missing Rating',
        text: 'Please select a rating.',
        icon: 'warning',
        confirmButtonColor: '#0288d1'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!await validateForm()) return;

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('content', formData.content.trim());
      formDataToSend.append('rating', formData.rating);

      if (isEditMode) {
        formDataToSend.append('updateImages', 'true');

        images.forEach(image => {
          formDataToSend.append('newImages', image);
        });

        imagesToRemove.forEach(imageId => {
          formDataToSend.append('removeImageIds', imageId);
        });
      } else {
        images.forEach(image => {
          formDataToSend.append('images', image);
        });
      }

      const url = isEditMode
        ? `${API_BASE_URL}/Reviews/${reviewId}`
        : `${API_BASE_URL}/Reviews`;

      const method = isEditMode ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        body: formDataToSend
      });

      if (response.ok) {
        await Swal.fire({
          title: 'Success!',
          text: isEditMode ? 'Review updated successfully!' : 'Review submitted successfully!',
          icon: 'success',
          timer: 3000,
          showConfirmButton: false
        });

        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit review');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Error',
        text: err.message,
        icon: 'error',
        confirmButtonColor: '#0288d1'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !reviewId) return;

    const result = await Swal.fire({
      title: 'Delete Review?',
      text: 'Are you sure you want to delete your review? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes, Delete'
    });

    if (result.isConfirmed) {
      setDeleting(true);

      try {
        const response = await authFetch(`${API_BASE_URL}/Reviews/${reviewId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          await Swal.fire({
            title: 'Deleted!',
            text: 'Your review has been deleted successfully.',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });

          navigate('/dashboard');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete review');
        }
      } catch (err) {
        await Swal.fire({
          title: 'Delete Failed',
          text: 'An error occurred while deleting your review. Please try again.',
          icon: 'error',
          confirmButtonColor: '#0288d1'
        });
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/review');
  };

  const renderStarRating = () => {
    return (
      <div className="write-review-star-rating">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            className={`write-review-star ${star <= (hoveredStar || formData.rating) ? 'active' : ''
              }`}
            onClick={() => handleStarClick(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
          >
            ★
          </button>
        ))}
        <span className="write-review-rating-text">
          {formData.rating > 0 && `${formData.rating} out of 5 stars`}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="write-review-container">
        <div className="write-review-loading">
          <Loading />
        </div>
      </div>
    );
  }

  // One review per user: any existing review is read-only, whatever its status. The check
  // is deliberately not a status whitelist - an unrecognised status must still lock the
  // form rather than fall through and let the customer submit a second review.
  // A rejected review is never surfaced as rejected: it renders as "being processed".
  if (isEditMode) {
    return (
      <div className="write-review-container">
        <div className="write-review-header">
          <h1 className="write-review-title">Your Review</h1>
          <p className="write-review-subtitle">
            {reviewStatus === 'Approved'
              ? 'Your review has been approved and is displayed on the website.'
              : 'Your review has been submitted.'
            }
          </p>
        </div>

        <div className="write-review-form-container">
          <div className="write-review-form">
            <div style={{
              padding: '15px 20px',
              borderRadius: '8px',
              marginBottom: '20px',
              backgroundColor: reviewStatus === 'Approved' ? '#d4edda' : '#fff3cd',
              color: reviewStatus === 'Approved' ? '#155724' : '#856404',
              border: `1px solid ${reviewStatus === 'Approved' ? '#28a745' : '#ffc107'}`
            }}>
              <i className={`fas ${reviewStatus === 'Approved' ? 'fa-check-circle' : 'fa-clock'}`} style={{ marginRight: '8px' }}></i>
              {reviewStatus === 'Approved'
                ? 'Your review is live and visible to other customers.'
                : 'Your review is currently being processed. Thank you for your feedback.'
              }
            </div>

            <div className="write-review-form-group">
              <label className="write-review-label">Rating</label>
              <div className="write-review-star-rating">
                {[1, 2, 3, 4, 5].map(star => (
                  <span key={star} className={`write-review-star ${star <= formData.rating ? 'active' : ''}`}>
                    ★
                  </span>
                ))}
                <span className="write-review-rating-text">{formData.rating} out of 5 stars</span>
              </div>
            </div>

            <div className="write-review-form-group">
              <label className="write-review-label">Title</label>
              <p style={{ margin: '5px 0', fontSize: '16px' }}>{formData.title}</p>
            </div>

            <div className="write-review-form-group">
              <label className="write-review-label">Review</label>
              <p style={{ margin: '5px 0', whiteSpace: 'pre-wrap' }}>{formData.content}</p>
            </div>

            {existingImages.length > 0 && (
              <div className="write-review-form-group">
                <label className="write-review-label">Photos</label>
                <div className="write-review-image-grid">
                  {existingImages.map(image => (
                    <div key={image.imageId} className="write-review-image-preview">
                      <img src={image.imageUrl} alt="Review" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="write-review-form-actions">
              <button
                type="button"
                className="write-review-cancel-btn"
                onClick={handleCancel}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create mode (no existing review)
  return (
    <div className="write-review-container">
      <div className="write-review-header">
        <h1 className="write-review-title">Write a Review</h1>
        <p className="write-review-subtitle">
          Share your experience with Ardency Construction & Trading
        </p>
      </div>

      <div className="write-review-form-container">
        <form onSubmit={handleSubmit} className="write-review-form">
          {/* Rating Section */}
          <div className="write-review-form-group">
            <label className="write-review-label required">
              Overall Rating
            </label>
            {renderStarRating()}
          </div>

          {/* Title Section */}
          <div className="write-review-form-group">
            <label htmlFor="title" className="write-review-label required">
              Review Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Summarize your experience..."
              className="write-review-input"
              maxLength={TITLE_MAX}
              required
            />
            <div className="write-review-char-count">
              {formData.title.length}/{TITLE_MAX} characters
            </div>
          </div>

          {/* Content Section */}
          <div className="write-review-form-group">
            <label htmlFor="content" className="write-review-label required">
              Your Review
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Tell us about your experience with our products and services..."
              className="write-review-textarea"
              maxLength={CONTENT_MAX}
              rows={6}
              required
            />
            <div className="write-review-char-count">
              {formData.content.length}/{CONTENT_MAX} characters
            </div>
            <p className="write-review-help-text">
              Be specific about your experience to help other customers
            </p>
          </div>

          {/* Images Section */}
          <div className="write-review-form-group">
            <label className="write-review-label">
              Add Photos (Optional)
            </label>

            {/* New Images Preview */}
            {previewImages.length > 0 && (
              <div className="write-review-new-images">
                <div className="write-review-image-grid">
                  {previewImages.map((preview, index) => (
                    <div key={index} className="write-review-image-preview">
                      <img src={preview.url} alt="Review preview" />
                      <div className="write-review-image-overlay">
                        <button
                          type="button"
                          className="write-review-image-remove"
                          onClick={() => removeNewImage(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Button */}
            {images.length < MAX_IMAGES && (
              <div className="write-review-upload-section">
                <input
                  type="file"
                  id="images"
                  multiple
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleImageUpload}
                  className="write-review-file-input"
                />
                <label htmlFor="images" className="write-review-upload-btn">
                  <i className="fas fa-camera"></i>
                  Add Photos
                </label>
              </div>
            )}

            <p className="write-review-help-text">
              <i className="fas fa-info-circle"></i>
              JPG, JPEG, PNG only • Maximum {MAX_IMAGES} images • Max 5MB per image
            </p>
          </div>

          <div className="write-review-form-actions">
            <button
              type="submit"
              className="write-review-submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  Submit Review
                </>
              )}
            </button>

            <button
              type="button"
              className="write-review-cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WriteReview;